import React, { useEffect, useMemo, useRef, useState } from "react";
import { Clapperboard, RefreshCw } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../components/layout/view/ViewHeader";
import Text from "../../../../components/typography/Text";
import ContentGrid from "../../../../components/grids/content/ContentGrid";
import EmptyState from "../../../../components/feedback/EmptyState";
import Button from "../../../../components/buttons/Button";

import PopThumbnailCard from "../../../../components/cards/pops/PopThumbnailCard";
import CreatorPopsFeed from "../../creators/views/CreatorPopsFeed";

import { useChannelPops } from "../hooks/useChannelPops";
import { useUserPopsViews } from "../../saved/users/hooks/useUserPopsViews";

import "./ChannelPopsView.css";

const LIMIT = 12;
const MAX_VIEW_IDS_PER_REQUEST = 50;

const validId = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const id = Number(value);

  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const normalizeCount = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const count = Number(value);

  return Number.isFinite(count) && count >= 0 ? count : fallback;
};

const normalizeViewResult = (result) => {
  const popId = validId(result?.popId ?? result?.pop_id);

  if (!popId) return null;

  const realViews = normalizeCount(
    result.realViews ?? result.real_views ?? result.views,
  );

  const demoViews = normalizeCount(result.demoViews ?? result.demo_views);

  const totalViews = normalizeCount(
    result.totalViews ?? result.total_views,
    realViews + demoViews,
  );

  return {
    popId,
    realViews,
    demoViews,
    totalViews,
  };
};

export default function ChannelPopsView({ username }) {
  const {
    pops,
    loading,
    loadingMore,
    searching,
    error,
    hasMore,
    searchQuery,
    searchCreatorChannelPops,
    loadMore,
    refresh,
  } = useChannelPops(username, LIMIT);

  const { getPopViewCounts, getPopViewCount } = useUserPopsViews();

  const [searchValue, setSearchValue] = useState("");
  const [selectedPopId, setSelectedPopId] = useState(null);
  const [viewCounts, setViewCounts] = useState({});

  const loadedViewIdsRef = useRef(new Set());
  const pendingViewIdsRef = useRef(new Set());
  const getPopViewCountsRef = useRef(getPopViewCounts);
  const mountedRef = useRef(false);
  const generationRef = useRef(0);
  const closePlayerRef = useRef(null);

  const popItems = useMemo(() => {
    const seen = new Set();

    return (Array.isArray(pops) ? pops : []).filter((pop) => {
      const popId = validId(pop?.popId);

      if (!popId || seen.has(popId)) return false;

      seen.add(popId);
      return true;
    });
  }, [pops]);

  const hasPops = popItems.length > 0;
  const isBusy = loading || searching || loadingMore;
  const isPlayerOpen = selectedPopId !== null;

  const errorMessage =
    typeof error === "string"
      ? error
      : error?.message || "Unable to load Pops.";

  getPopViewCountsRef.current = getPopViewCounts;

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    generationRef.current += 1;

    loadedViewIdsRef.current.clear();
    pendingViewIdsRef.current.clear();

    setViewCounts({});
    setSelectedPopId(null);
    setSearchValue("");
  }, [username]);

  const applyViewCounts = (results) => {
    if (!Array.isArray(results)) return;

    const nextCounts = {};

    results.forEach((result) => {
      const normalized = normalizeViewResult(result);

      if (!normalized) return;

      const key = String(normalized.popId);

      nextCounts[key] = normalized;
      loadedViewIdsRef.current.add(key);
    });

    if (Object.keys(nextCounts).length) {
      setViewCounts((previous) => ({
        ...previous,
        ...nextCounts,
      }));
    }
  };

  const loadViewCounts = async (
    ids,
    { force = false, generation = generationRef.current } = {},
  ) => {
    const validIds = [...new Set(ids.map(validId).filter(Boolean))];

    const requestedIds = validIds.filter((id) => {
      const key = String(id);

      if (pendingViewIdsRef.current.has(key)) return false;

      return force || !loadedViewIdsRef.current.has(key);
    });

    if (!requestedIds.length) return;

    const batches = [];

    for (
      let index = 0;
      index < requestedIds.length;
      index += MAX_VIEW_IDS_PER_REQUEST
    ) {
      batches.push(requestedIds.slice(index, index + MAX_VIEW_IDS_PER_REQUEST));
    }

    requestedIds.forEach((id) => {
      pendingViewIdsRef.current.add(String(id));
    });

    await Promise.all(
      batches.map(async (batch) => {
        try {
          const results = await getPopViewCountsRef.current(batch);

          if (!mountedRef.current || generation !== generationRef.current) {
            return;
          }

          if (!Array.isArray(results)) {
            throw new Error("Invalid Pop view-count response.");
          }

          applyViewCounts(results);
        } catch (viewError) {
          console.error("Unable to retrieve channel Pop views:", viewError);
        } finally {
          if (generation === generationRef.current) {
            batch.forEach((id) => {
              pendingViewIdsRef.current.delete(String(id));
            });
          }
        }
      }),
    );
  };

  useEffect(() => {
    if (!popItems.length) return;

    void loadViewCounts(popItems.map((pop) => pop.popId));
  }, [popItems, username]);

  const refreshViewCounts = async () => {
    const ids = popItems.map((pop) => pop.popId);

    if (!ids.length) return;

    await loadViewCounts(ids, { force: true });
  };

  const handleRefresh = async () => {
    const result = await refresh();

    if (result !== false) {
      await refreshViewCounts();
    }
  };

  const handleOpenPop = (pop) => {
    const popId = validId(pop?.popId);

    if (!popId) return;

    setSelectedPopId(popId);
  };

  const handleClosePop = () => {
    setSelectedPopId(null);
    void refreshViewCounts();
  };

  closePlayerRef.current = handleClosePop;

  const handleVideoError = (pop, videoError) => {
    console.error("Channel Pop playback error:", pop?.popId, videoError);
  };

  const handleLoadMore = () => {
    if (loading || searching || loadingMore || !hasMore) {
      return false;
    }

    return loadMore();
  };

  useEffect(() => {
    if (!isPlayerOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closePlayerRef.current?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isPlayerOpen]);

  useEffect(() => {
    if (selectedPopId === null || loading || searching) return;

    const exists = popItems.some(
      (pop) => String(pop.popId) === String(selectedPopId),
    );

    if (!exists) {
      setSelectedPopId(null);
    }
  }, [popItems, selectedPopId, loading, searching]);

  const renderPop = (pop) => {
    const fetched = viewCounts[String(pop.popId)];

    const fallbackRealViews = normalizeCount(
      pop.realViews ?? pop.real_views ?? pop.views,
    );

    const realViews = fetched
      ? fetched.realViews
      : getPopViewCount(pop.popId, fallbackRealViews);

    const demoViews = fetched
      ? fetched.demoViews
      : normalizeCount(pop.demoViews ?? pop.demo_views);

    const totalViews = fetched
      ? fetched.totalViews
      : normalizeCount(
          pop.totalViews ?? pop.total_views,
          realViews + demoViews,
        );

    const displayPop = {
      ...pop,
      views: totalViews,
      realViews,
      demoViews,
      totalViews,
    };

    return (
      <PopThumbnailCard
        key={pop.popId ?? pop.popTag}
        pop={displayPop}
        mode="grid"
        onClick={() => handleOpenPop(pop)}
      />
    );
  };

  const renderContent = () => {
    if ((loading || searching) && !hasPops) {
      return (
        <ContentGrid
          items={[]}
          loading
          columns={6}
          tabletColumns={3}
          mobileColumns={2}
          gap={16}
          skeletonCount={LIMIT}
        />
      );
    }

    if (error && !hasPops) {
      return (
        <EmptyState
          icon={<Clapperboard size={28} />}
          title="Unable to load Pops"
          description={errorMessage}
          action={
            <Button
              value="Try Again"
              icon={<RefreshCw size={16} />}
              variant="primary-outlined"
              disabled={isBusy}
              onClick={handleRefresh}
            />
          }
        />
      );
    }

    if (!hasPops) {
      return (
        <EmptyState
          icon={<Clapperboard size={28} />}
          title={searchQuery ? "No Pops found" : "No Pops yet"}
          description={
            searchQuery
              ? "Try another search."
              : "This creator has not shared any Pops yet."
          }
        />
      );
    }

    return (
      <ContentGrid
        items={popItems}
        columns={6}
        tabletColumns={3}
        mobileColumns={2}
        gap={16}
        showEmpty={false}
        renderItem={renderPop}
      />
    );
  };

  return (
    <ViewContainer>
      <ViewHeader title="Pops" description="Explore Pops and content." />

      {error && hasPops && (
        <div className="channel-pops__error" role="alert">
          <Text as="p" size="sm">
            {errorMessage}
          </Text>
        </div>
      )}

      <div className="channel-pops__content">{renderContent()}</div>

      {hasMore && hasPops && (
        <div className="channel-pops__footer">
          <Button
            value={loadingMore ? "Loading..." : "Load More"}
            variant="primary-outlined"
            disabled={isBusy}
            onClick={handleLoadMore}
          />
        </div>
      )}

      {isPlayerOpen && (
        <CreatorPopsFeed
          key={selectedPopId}
          pops={popItems}
          initialPopId={selectedPopId}
          fullscreen
          onClose={handleClosePop}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
          onVideoError={handleVideoError}
          showCreator
          showActions={false}
        />
      )}
    </ViewContainer>
  );
}
